import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useId, useRef, useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import { useDialogFocus } from "../../app/useDialogFocus";
import { DataWorkspace } from "../shared/DataWorkspace";
import { TeacherPlannedLessonsText } from "../shared/TeacherPlannedLessons";
import { CatalogImportActions } from "./CatalogImportActions";

interface CourseItem {
  id: string;
  name: string;
  sort_order: number;
}

interface RoomInstance {
  id: string;
  ordinal: number;
  display_name: string;
}

interface RoomItem extends CourseItem {
  count: number;
  instances: RoomInstance[];
}

interface CatalogList<T> {
  items: T[];
  condition_revision: number;
  locked: boolean;
}

interface TeacherItem {
  id: string;
  source_id: string;
  name: string;
  planned_lessons: number | null;
  max_daily_lessons: number | null;
  sort_order: number;
}

interface ClassItem {
  id: string;
  source_id: string;
  grade_code: number;
  grade_label: string;
  class_name: string;
  sort_order: number;
}

const GRADE_LABELS = [
  "小班",
  "中班",
  "大班",
  "一年级",
  "二年级",
  "三年级",
  "四年级",
  "五年级",
  "六年级",
  "七年级",
  "八年级",
  "九年级",
  "高中一年级",
  "高中二年级",
  "高中三年级"
] as const;

export function PageFrame({
  projectId,
  title,
  description,
  action,
  eyebrow = "排课条件",
  wide = false,
  children
}: {
  projectId: string;
  title: string;
  description: string;
  action?: React.ReactNode;
  eyebrow?: string;
  wide?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <main className={`page-shell ${wide ? "page-shell-wide" : ""}`}>
      <a className="back-link" href={`/projects/${projectId}`}>
        ← 返回课表主页
      </a>
      <header className="page-header catalog-heading">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p className="lede">{description}</p>
        </div>
        {action}
      </header>
      {children}
    </main>
  );
}

export function LockedNotice(): React.JSX.Element {
  return (
    <section className="locked-notice">
      当前课表已完成自动排课，排课条件不可修改。如需调整，请从首页复制该课表。
    </section>
  );
}

export function ErrorText({ error }: { error: unknown }): React.JSX.Element | null {
  if (!error) return null;
  return (
    <p className="error-message" role="alert">
      {error instanceof Error ? error.message : "操作失败，请稍后重试"}
    </p>
  );
}

export function ErrorDialog({
  title = "保存失败",
  error,
  onClose
}: {
  title?: string;
  error: unknown;
  onClose: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      const returnFocus =
        previousFocus && previousFocus !== document.body && previousFocus.isConnected
          ? previousFocus
          : document.querySelector<HTMLElement>(
              ".modal-card[role='dialog'] button[type='submit']:not([disabled])"
            );
      requestAnimationFrame(() => returnFocus?.focus());
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="error-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <h2 id={titleId}>{title}</h2>
      <div className="error-dialog-message" id={descriptionId}>
        <span className="error-dialog-icon" aria-hidden="true">!</span>
        <ErrorText error={error} />
      </div>
      <div className="modal-actions">
        <button className="primary-button" type="button" onClick={onClose}>
          返回修改
        </button>
      </div>
    </dialog>
  );
}

export function CoursesPage({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CourseItem | "new" | null>(null);
  const [deleting, setDeleting] = useState<CourseItem | null>(null);
  const [name, setName] = useState("");
  const courses = useQuery({
    queryKey: ["courses", projectId],
    queryFn: () => apiRequest<CatalogList<CourseItem>>(`/projects/${projectId}/courses`)
  });
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      if (!courses.data) return;
      if (action === "delete" && deleting) {
        return apiRequest(`/projects/${projectId}/courses/${deleting.id}?expected_condition_revision=${courses.data.condition_revision}&confirm_impacts=true`, {
          method: "DELETE"
        });
      }
      if (!editing) return;
      const path =
        editing === "new"
          ? `/projects/${projectId}/courses`
          : `/projects/${projectId}/courses/${editing.id}`;
      return apiRequest(path, {
        method: editing === "new" ? "POST" : "PATCH",
        body: JSON.stringify({
          name,
          expected_condition_revision: courses.data.condition_revision
        })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["courses", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      setEditing(null);
      setDeleting(null);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({ queryKey: ["courses", projectId] });
      }
    }
  });

  const data = courses.data;
  return (
    <DataWorkspace
      projectId={projectId}
      active="courses"
    >
      {data?.locked ? <LockedNotice /> : null}
      <section className="content-card data-card" aria-busy={courses.isLoading}>
        <div className="data-toolbar">
          {data ? (
            <span className="data-toolbar-note">
              共 {data.items.length} 门课程
            </span>
          ) : null}
          <div className="data-toolbar-actions">
            <CatalogImportActions
              projectId={projectId}
              kind="courses"
              conditionRevision={data?.condition_revision}
              locked={data?.locked ?? true}
              listQueryKey={["courses", projectId]}
            />
            <button
              className="solid-button"
              type="button"
              disabled={!data || data.locked}
              onClick={() => {
                setName("");
                setEditing("new");
              }}
            >
              添加课程
            </button>
          </div>
        </div>
        {courses.isLoading ? <p className="state-message">正在加载课程…</p> : null}
        {courses.isError ? <ErrorText error={courses.error} /> : null}
        {data && data.items.length === 0 ? (
          <div className="empty-card compact">
            <h2>还没有课程</h2>
          </div>
        ) : null}
        {data?.items.length ? (
          <table className="data-table">
            <thead><tr><th>课程名称</th><th className="actions-column">操作</th></tr></thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    <div className="row-actions">
                      <button className="text-button" type="button" disabled={data.locked} onClick={() => { setName(item.name); setEditing(item); }}>编辑</button>
                      <button className="text-button danger" type="button" disabled={data.locked} onClick={() => setDeleting(item)}>删除</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
      {editing ? (
        <EditorModal
          title={editing === "new" ? "添加课程" : "编辑课程"}
          onClose={() => setEditing(null)}
          onSubmit={() => mutation.mutate("save")}
          pending={mutation.isPending}
          error={mutation.error}
        >
          <label className="field"><span>课程名称</span><input value={name} onChange={(event) => setName(event.target.value)} /></label>
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal
          title="删除课程"
          description={`删除“${deleting.name}”后，引用它的任课计划会显示为待完善；旧课表仍保留原课程名称并标注已删除。`}
          dangerLabel="确认删除"
          onClose={() => setDeleting(null)}
          onConfirm={() => mutation.mutate("delete")}
          pending={mutation.isPending}
          error={mutation.error}
        />
      ) : null}
    </DataWorkspace>
  );
}

export function RoomsPage({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RoomItem | "new" | null>(null);
  const [deleting, setDeleting] = useState<RoomItem | null>(null);
  const [name, setName] = useState("");
  const [count, setCount] = useState(1);
  const rooms = useQuery({
    queryKey: ["rooms", projectId],
    queryFn: () => apiRequest<CatalogList<RoomItem>>(`/projects/${projectId}/room-types`)
  });
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      if (!rooms.data) return;
      if (action === "delete" && deleting) {
        return apiRequest(`/projects/${projectId}/room-types/${deleting.id}?expected_condition_revision=${rooms.data.condition_revision}&confirm_impacts=true`, { method: "DELETE" });
      }
      if (!editing) return;
      const path = editing === "new" ? `/projects/${projectId}/room-types` : `/projects/${projectId}/room-types/${editing.id}`;
      return apiRequest(path, {
        method: editing === "new" ? "POST" : "PATCH",
        body: JSON.stringify({
          name,
          count,
          expected_condition_revision: rooms.data.condition_revision,
          confirm_impacts: true
        })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rooms", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      setEditing(null);
      setDeleting(null);
    }
  });
  const data = rooms.data;
  return (
    <DataWorkspace
      projectId={projectId}
      active="rooms"
    >
      {data?.locked ? <LockedNotice /> : null}
      <section className="content-card data-card">
        <div className="data-toolbar">
          {data ? (
            <span className="data-toolbar-note">
              共 {data.items.length} 类功能教室
            </span>
          ) : null}
          <div className="data-toolbar-actions">
            <CatalogImportActions
              projectId={projectId}
              kind="rooms"
              conditionRevision={data?.condition_revision}
              locked={data?.locked ?? true}
              listQueryKey={["rooms", projectId]}
            />
            <button className="solid-button" type="button" disabled={!data || data.locked} onClick={() => { setName(""); setCount(1); setEditing("new"); }}>添加功能教室</button>
          </div>
        </div>
        {rooms.isLoading ? <p className="state-message">正在加载功能教室…</p> : null}
        {rooms.isError ? <ErrorText error={rooms.error} /> : null}
        {data && data.items.length === 0 ? <div className="empty-card compact"><h2>还没有功能教室</h2><p>仅在课程需要占用专用空间时添加。</p></div> : null}
        {data?.items.length ? (
          <table className="data-table">
            <thead><tr><th>功能教室名称</th><th>间数</th><th>具体功能教室</th><th className="actions-column">操作</th></tr></thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td><td>{item.count}</td>
                  <td>{item.instances.map((instance) => instance.display_name).join("、")}</td>
                  <td><div className="row-actions">
                    <button className="text-button" type="button" disabled={data.locked} onClick={() => { setName(item.name); setCount(item.count); setEditing(item); }}>编辑</button>
                    <button className="text-button danger" type="button" disabled={data.locked} onClick={() => setDeleting(item)}>删除</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
      {editing ? (
        <EditorModal title={editing === "new" ? "添加功能教室" : "编辑功能教室"} onClose={() => setEditing(null)} onSubmit={() => mutation.mutate("save")} pending={mutation.isPending} error={mutation.error}>
          <label className="field"><span>功能教室名称</span><input value={name} onChange={(event) => setName(event.target.value)} /></label>
          <label className="field"><span>间数</span><input type="number" min={1} value={count} onChange={(event) => setCount(Number(event.target.value))} /></label>
          <p className="field-hint">减少间数时从最大序号开始移除；旧课表中的功能教室记录会保留。</p>
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal title="删除功能教室" description={`删除“${deleting.name}”会同时停用其中 ${deleting.count} 间具体功能教室；任课计划中的可选功能教室将回退为“无”。`} dangerLabel="确认删除" onClose={() => setDeleting(null)} onConfirm={() => mutation.mutate("delete")} pending={mutation.isPending} error={mutation.error} />
      ) : null}
    </DataWorkspace>
  );
}

export function TeachersPage({ projectId }: { projectId: string }): React.JSX.Element {
  return <PeoplePage projectId={projectId} kind="teachers" />;
}

export function ClassesPage({ projectId }: { projectId: string }): React.JSX.Element {
  return <PeoplePage projectId={projectId} kind="classes" />;
}

function PeoplePage({
  projectId,
  kind
}: {
  projectId: string;
  kind: "teachers" | "classes";
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const isTeacher = kind === "teachers";
  const [editing, setEditing] = useState<TeacherItem | ClassItem | "new" | null>(null);
  const [deleting, setDeleting] = useState<TeacherItem | ClassItem | null>(null);
  const [teacherName, setTeacherName] = useState("");
  const [plannedLessons, setPlannedLessons] = useState("");
  const [maxDailyLessons, setMaxDailyLessons] = useState("");
  const [gradeLabel, setGradeLabel] = useState<(typeof GRADE_LABELS)[number]>("一年级");
  const [className, setClassName] = useState("");
  const current = useQuery({
    queryKey: [kind, projectId],
    queryFn: () =>
      apiRequest<CatalogList<TeacherItem | ClassItem>>(
        `/projects/${projectId}/${kind}`
      )
  });
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      if (!current.data) return;
      if (action === "delete" && deleting) {
        return apiRequest(
          `/projects/${projectId}/${kind}/${deleting.id}?expected_condition_revision=${current.data.condition_revision}&confirm_impacts=true`,
          { method: "DELETE" }
        );
      }
      if (!editing) return;
      const path =
        editing === "new"
          ? `/projects/${projectId}/${kind}`
          : `/projects/${projectId}/${kind}/${editing.id}`;
      return apiRequest(path, {
        method: editing === "new" ? "POST" : "PATCH",
        body: JSON.stringify({
          ...(isTeacher
            ? {
                name: teacherName,
                planned_lessons:
                  plannedLessons === "" ? null : Number(plannedLessons),
                max_daily_lessons:
                  maxDailyLessons === "" ? null : Number(maxDailyLessons)
              }
            : { grade_label: gradeLabel, class_name: className }),
          expected_condition_revision: current.data.condition_revision
        })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [kind, projectId] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      setEditing(null);
      setDeleting(null);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "REVISION_CONFLICT") {
        await queryClient.invalidateQueries({ queryKey: [kind, projectId] });
        await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      }
    }
  });
  const data = current.data;
  const label = isTeacher ? "教师" : "班级";

  function openNew(): void {
    setTeacherName("");
    setPlannedLessons("");
    setMaxDailyLessons("");
    setGradeLabel("一年级");
    setClassName("");
    setEditing("new");
  }

  function openEdit(item: TeacherItem | ClassItem): void {
    if (isTeacher) {
      const teacher = item as TeacherItem;
      setTeacherName(teacher.name);
      setPlannedLessons(
        teacher.planned_lessons === null ? "" : String(teacher.planned_lessons)
      );
      setMaxDailyLessons(
        teacher.max_daily_lessons === null
          ? ""
          : String(teacher.max_daily_lessons)
      );
    } else {
      const schoolClass = item as ClassItem;
      setGradeLabel(schoolClass.grade_label as (typeof GRADE_LABELS)[number]);
      setClassName(schoolClass.class_name);
    }
    setEditing(item);
  }

  return (
    <DataWorkspace
      projectId={projectId}
      active={kind}
    >
      {data?.locked ? <LockedNotice /> : null}
      <section className="content-card data-card" aria-busy={current.isLoading}>
        <div className="data-toolbar">
          {data ? (
            <span className="data-toolbar-note">
              共 {data.items.length} {isTeacher ? "位教师" : "个班级"}
            </span>
          ) : null}
          <div className="data-toolbar-actions">
            <CatalogImportActions
              projectId={projectId}
              kind={kind}
              conditionRevision={data?.condition_revision}
              locked={data?.locked ?? true}
              listQueryKey={[kind, projectId]}
            />
            <button
              className="solid-button"
              type="button"
              disabled={!data || data.locked}
              onClick={openNew}
            >
              添加{label}
            </button>
          </div>
        </div>
        {current.isLoading ? <p className="state-message">正在加载{label}…</p> : null}
        {current.isError ? <ErrorText error={current.error} /> : null}
        {data && data.items.length === 0 ? (
          <div className="empty-card compact">
            <h2>还没有{label}信息</h2>
          </div>
        ) : null}
        {data?.items.length ? (
          <table className="data-table">
            <thead>
              <tr>
                {isTeacher ? <><th>教师姓名</th><th>计划每周课时数</th><th>每天最大课时数</th></> : <><th>年级</th><th>班级名称</th></>}
                <th className="actions-column">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  {isTeacher ? (
                    <>
                      <td>{(item as TeacherItem).name}</td>
                      <td>
                        <TeacherPlannedLessonsText
                          value={(item as TeacherItem).planned_lessons}
                        />
                      </td>
                      <td>
                        {(item as TeacherItem).max_daily_lessons === null
                          ? "不限"
                          : `${(item as TeacherItem).max_daily_lessons} 节`}
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{(item as ClassItem).grade_label}</td>
                      <td>{(item as ClassItem).class_name}</td>
                    </>
                  )}
                  <td>
                    <div className="row-actions">
                      <button className="text-button" type="button" disabled={data.locked} onClick={() => openEdit(item)}>编辑</button>
                      <button className="text-button danger" type="button" disabled={data.locked} onClick={() => setDeleting(item)}>删除</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
      {editing ? (
        <EditorModal
          title={`${editing === "new" ? "添加" : "编辑"}${label}`}
          onClose={() => setEditing(null)}
          onSubmit={() => mutation.mutate("save")}
          pending={mutation.isPending}
          error={mutation.error}
        >
          {isTeacher ? (
            <>
              <label className="field"><span>教师姓名</span><input value={teacherName} onChange={(event) => setTeacherName(event.target.value)} /></label>
              <label className="field">
                <span>课时数</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="例如：18"
                  value={plannedLessons}
                  onChange={(event) => setPlannedLessons(event.target.value)}
                />
              </label>
              <label className="field">
                <span>每天最大课时数</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="留空表示不限"
                  value={maxDailyLessons}
                  onChange={(event) => setMaxDailyLessons(event.target.value)}
                />
              </label>
            </>
          ) : (
            <>
              <label className="field"><span>年级</span><select value={gradeLabel} onChange={(event) => setGradeLabel(event.target.value as (typeof GRADE_LABELS)[number])}>{GRADE_LABELS.map((grade) => <option value={grade} key={grade}>{grade}</option>)}</select></label>
              <label className="field"><span>班级名称</span><input value={className} onChange={(event) => setClassName(event.target.value)} placeholder="例如：1班" /></label>
            </>
          )}
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal
          title={`删除${label}`}
          description={`删除“${isTeacher ? (deleting as TeacherItem).name : `${(deleting as ClassItem).grade_label}${(deleting as ClassItem).class_name}`}”后，相关任课计划可能需要完善；旧课表仍保留原信息。`}
          dangerLabel="确认删除"
          onClose={() => setDeleting(null)}
          onConfirm={() => mutation.mutate("delete")}
          pending={mutation.isPending}
          error={mutation.error}
        />
      ) : null}
    </DataWorkspace>
  );
}

export function EditorModal({
  title,
  children,
  onClose,
  onSubmit,
  pending,
  error,
  submitLabel = "保存",
  submitDisabled = false,
  wide = false,
  className = ""
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  submitLabel?: string;
  submitDisabled?: boolean;
  wide?: boolean;
  className?: string;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, !pending);
  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit();
  }
  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className={`modal-card ${wide ? "wide" : ""} ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby="editor-title">
        <form className="editor-form" onSubmit={submit}>
          <h2 id="editor-title">{title}</h2>
          <div className="modal-body">
            <div className="form-stack">{children}</div>
          </div>
          <div className="modal-action-error"><ErrorText error={error} /></div>
          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose}>取消</button>
            <button className="primary-button" type="submit" disabled={pending || submitDisabled}>{pending ? "正在保存…" : submitLabel}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function ConfirmModal({
  title,
  description,
  dangerLabel,
  onClose,
  onConfirm,
  pending,
  error
}: {
  title: string;
  description: string;
  dangerLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: unknown;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, !pending);
  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className="modal-card" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 id="confirm-title">{title}</h2>
        <p>{description}</p>
        <ErrorText error={error} />
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>取消</button>
          <button className="primary-button danger-button" type="button" disabled={pending} onClick={onConfirm}>{pending ? "正在处理…" : dangerLabel}</button>
        </div>
      </section>
    </div>
  );
}
